import React, { useEffect, useState } from 'react';
import { COASegment, AccountType } from './ChartOfAccounts';
import { AccountTypeBadge } from './AccountTypeBadge';
import { SaveIcon, RefreshCwIcon } from 'lucide-react';
interface SegmentEditorProps {
  segment: COASegment | null;
  accountTypes: AccountType[];
  isNew: boolean;
  onSave: (segment: COASegment) => void;
  onCancel: () => void;
  locale: string;
}
export function SegmentEditor({
  segment,
  accountTypes,
  isNew,
  onSave,
  onCancel,
  locale
}: SegmentEditorProps) {
  const [formData, setFormData] = useState<COASegment>({
    id: '',
    segmentCode: '',
    segmentName: '',
    accountTypeId: '',
    isActive: true,
    activeFrom: new Date().toISOString().split('T')[0],
    fullGLPath: '',
    level: 1
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Initialize form data when segment changes
  useEffect(() => {
    if (segment) {
      setFormData({
        ...segment,
        activeFrom: segment.activeFrom || new Date().toISOString().split('T')[0]
      });
    } else {
      // Default values for new segment
      setFormData({
        id: '',
        segmentCode: '',
        segmentName: '',
        accountTypeId: accountTypes.length > 0 ? accountTypes[0].id : '',
        isActive: true,
        activeFrom: new Date().toISOString().split('T')[0],
        fullGLPath: '',
        level: 1
      });
    }
  }, [segment, accountTypes]);
  // Handle form changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    });
    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Validate form
    const newErrors: Record<string, string> = {};
    if (!formData.segmentCode.trim()) {
      newErrors.segmentCode = 'Segment Code is required';
    }
    if (!formData.segmentName.trim()) {
      newErrors.segmentName = 'Segment Name is required';
    }
    if (!formData.accountTypeId) {
      newErrors.accountTypeId = 'Account Type is required';
    }
    if (formData.activeTo && formData.activeFrom && new Date(formData.activeTo) < new Date(formData.activeFrom)) {
      newErrors.activeTo = 'Active To date must be after Active From date';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    // Calculate fullGLPath if it's a new segment
    let fullGLPath = formData.segmentCode;
    if (formData.parentId && segment?.parentId) {
      // For child segments, we need to construct the path
      const parentPath = segment.fullGLPath.split('.').slice(0, -1).join('.');
      fullGLPath = parentPath ? `${parentPath}.${formData.segmentCode}` : formData.segmentCode;
    }
    onSave({
      ...formData,
      fullGLPath
    });
  };
  // Recalculate path
  const handleRecalculatePath = () => {
    // This is a simplified version - in a real app, this would call the backend
    // to recalculate the path based on the current parent-child relationships
    if (formData.parentId) {
      // For now, just update the local state to show the concept
      const parentSegment = segment?.fullGLPath.split('.').slice(0, -1).join('.');
      const newPath = parentSegment ? `${parentSegment}.${formData.segmentCode}` : formData.segmentCode;
      setFormData({
        ...formData,
        fullGLPath: newPath
      });
    } else {
      setFormData({
        ...formData,
        fullGLPath: formData.segmentCode
      });
    }
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-9618">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-9619">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-9620">
          {isNew ? 'Create New Segment' : 'Edit Segment'}
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-9621">
          {isNew ? 'Enter details for the new segment' : 'Update segment details'}
        </p>
      </div>
      <div className="px-4 py-5 sm:p-6" data-id="element-9622">
        <form onSubmit={handleSubmit} data-id="element-9623">
          <div className="space-y-6" data-id="element-9624">
            {/* Segment Code */}
            <div data-id="element-9625">
              <label htmlFor="segmentCode" className="block text-sm font-medium text-gray-700" data-id="element-9626">
                Segment Code *
              </label>
              <div className="mt-1" data-id="element-9627">
                <input type="text" name="segmentCode" id="segmentCode" value={formData.segmentCode} onChange={handleChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.segmentCode ? 'border-red-300' : ''}`} data-id="element-9628" />
                {errors.segmentCode && <p className="mt-1 text-sm text-red-600" data-id="element-9629">
                    {errors.segmentCode}
                  </p>}
              </div>
            </div>
            {/* Segment Name */}
            <div data-id="element-9630">
              <label htmlFor="segmentName" className="block text-sm font-medium text-gray-700" data-id="element-9631">
                Segment Name *
              </label>
              <div className="mt-1" data-id="element-9632">
                <input type="text" name="segmentName" id="segmentName" value={formData.segmentName} onChange={handleChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.segmentName ? 'border-red-300' : ''}`} data-id="element-9633" />
                {errors.segmentName && <p className="mt-1 text-sm text-red-600" data-id="element-9634">
                    {errors.segmentName}
                  </p>}
              </div>
            </div>
            {/* Account Type */}
            <div data-id="element-9635">
              <label htmlFor="accountTypeId" className="block text-sm font-medium text-gray-700" data-id="element-9636">
                Account Type *
              </label>
              <div className="mt-1" data-id="element-9637">
                <select id="accountTypeId" name="accountTypeId" value={formData.accountTypeId} onChange={handleChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.accountTypeId ? 'border-red-300' : ''}`} data-id="element-9638">
                  <option value="" data-id="element-9639">Select an account type</option>
                  {accountTypes.map(type => <option key={type.id} value={type.id} data-id="element-9640">
                      {type.name}
                    </option>)}
                </select>
                {errors.accountTypeId && <p className="mt-1 text-sm text-red-600" data-id="element-9641">
                    {errors.accountTypeId}
                  </p>}
              </div>
              {formData.accountTypeId && <div className="mt-2" data-id="element-9642">
                  <AccountTypeBadge type={accountTypes.find(at => at.id === formData.accountTypeId)!} size="md" data-id="element-9643" />
                </div>}
            </div>
            {/* Parent Segment (read-only) */}
            {formData.parentId && <div data-id="element-9644">
                <label className="block text-sm font-medium text-gray-700" data-id="element-9645">
                  Parent Segment
                </label>
                <div className="mt-1" data-id="element-9646">
                  <input type="text" value={segment?.parentId ? `${segment.fullGLPath.split('.').slice(0, -1).join('.')}` : ''} readOnly className="bg-gray-50 shadow-sm block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-9647" />
                </div>
              </div>}
            {/* Full GL Path */}
            <div data-id="element-9648">
              <div className="flex justify-between items-center" data-id="element-9649">
                <label className="block text-sm font-medium text-gray-700" data-id="element-9650">
                  Full GL Path
                </label>
                <button type="button" onClick={handleRecalculatePath} className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9651">
                  <RefreshCwIcon className="h-3 w-3 mr-1" data-id="element-9652" />
                  Recalculate
                </button>
              </div>
              <div className="mt-1" data-id="element-9653">
                <input type="text" value={formData.fullGLPath} readOnly className="bg-gray-50 shadow-sm block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-9654" />
              </div>
            </div>
            {/* Active Status */}
            <div className="flex items-center" data-id="element-9655">
              <input id="isActive" name="isActive" type="checkbox" checked={formData.isActive} onChange={handleChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-9656" />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700" data-id="element-9657">
                Active
              </label>
            </div>
            {/* Active From */}
            <div data-id="element-9658">
              <label htmlFor="activeFrom" className="block text-sm font-medium text-gray-700" data-id="element-9659">
                Active From
              </label>
              <div className="mt-1" data-id="element-9660">
                <input type="date" name="activeFrom" id="activeFrom" value={formData.activeFrom} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-9661" />
              </div>
            </div>
            {/* Active To */}
            <div data-id="element-9662">
              <label htmlFor="activeTo" className="block text-sm font-medium text-gray-700" data-id="element-9663">
                Active To
              </label>
              <div className="mt-1" data-id="element-9664">
                <input type="date" name="activeTo" id="activeTo" value={formData.activeTo || ''} onChange={handleChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.activeTo ? 'border-red-300' : ''}`} data-id="element-9665" />
                {errors.activeTo && <p className="mt-1 text-sm text-red-600" data-id="element-9666">{errors.activeTo}</p>}
              </div>
            </div>
            {/* Org Unit (optional) */}
            <div data-id="element-9667">
              <label htmlFor="orgUnitId" className="block text-sm font-medium text-gray-700" data-id="element-9668">
                Org Unit (optional)
              </label>
              <div className="mt-1" data-id="element-9669">
                <select id="orgUnitId" name="orgUnitId" value={formData.orgUnitId || ''} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-9670">
                  <option value="" data-id="element-9671">None</option>
                  <option value="org-001" data-id="element-9672">Corporate</option>
                  <option value="org-002" data-id="element-9673">North America</option>
                  <option value="org-003" data-id="element-9674">Europe</option>
                  <option value="org-004" data-id="element-9675">Asia Pacific</option>
                </select>
              </div>
            </div>
            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4" data-id="element-9676">
              <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9677">
                Cancel
              </button>
              <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9678">
                <SaveIcon className="h-4 w-4 mr-2" data-id="element-9679" />
                {isNew ? 'Create Segment' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>;
}